import React, {
    createContext, useContext, useState, useEffect,
} from 'react';
import { useSiteInfo } from '@contexts/SiteInfoContext';
import insertAgentId from '@utils/insertAgentId';

const RegisterContext = createContext();

export const useRegisterContext = () => {
    const context = useContext(RegisterContext);
    if (!context) {
        throw new Error('useRegisterContext must be used within a RegisterProvider');
    }
    return context;
};

export const RegisterProvider = ({ children }) => {
    // 存儲從 Login 傳來的資料
    const [loginTempData, setLoginTempData] = useState({
        password: '',
        custToken: '',
    });

    const { agentId } = useSiteInfo();
    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState({
        agentId: '',
        userName: '',
        userpwd: '',
        userpwdOld: '',
        userpwdConfirm: '',
        firstName: '',
        lastName: '',
        countryCode: '',
        phoneNumber: '',
        mobilePhone: '',
        domain: '',
        custToken: '',
    });

    useEffect(() => {
        if (typeof window !== 'undefined' && agentId) {
            const devDomain = insertAgentId(window.location.href);
            setFormData((prev) => ({
                ...prev,
                agentId: agentId.toString(),
                domain: `https://${devDomain}`,
            }));
        }
    }, [agentId]);

    useEffect(() => {
        setFormData((prev) => ({
            ...prev,
            custToken: loginTempData.custToken,
        }));
    }, [loginTempData.custToken]);

    const [formError, setFormError] = useState({
        userName: false,
        userNameAvailable: false,
        userpwd: false,
        userpwdOld: false,
        userpwdConfirm: false,
        userpwdDifference: false,
        firstName: false,
        firstNameLength: false,
        lastName: false,
        lastNameLength: false,
        countryCode: false,
        phoneNumber: false,
    });

    const handleInputChange = (input) => (e) => {
        let { value } = e.target;

        // 對電話相關欄位進行數值驗證
        if (input === 'countryCode' || input === 'phoneNumber') {
            value = value.replace(/[^0-9]/g, '');
        }

        // 組合 mobile 欄位
        setFormData((item) => {
            const newData = {
                ...item,
                [input]: value,
            };

            if (input === 'countryCode' || input === 'phoneNumber') {
                const countryCode = input === 'countryCode' ? value : item.countryCode;
                const phoneNumber = input === 'phoneNumber' ? value : item.phoneNumber;

                newData.mobilePhone = `${countryCode}-${phoneNumber}`;
            }

            return newData;
        });

        setFormError((item) => {
            const newError = {
                ...item,
                [input]: false,
            };

            if (input === 'userName') {
                newError.userNameAvailable = false;
            } else if (input === 'firstName') {
                newError.firstNameLength = false;
            } else if (input === 'lastName') {
                newError.lastNameLength = false;
            }

            if (input === 'userpwd' || input === 'userpwdConfirm') {
                newError.userpwdDifference = false;
                newError.userpwdConfirm = false;
            }

            return newError;
        });
    };

    // Modal - 註冊 Loading
    const [registerLoading, setRegisterLoading] = useState(false);

    // Modal - 註冊回應
    const [registerResponse, setRegisterResponse] = useState({
        code: null,
        message: '',
        showModal: false,
    });

    // Modal - 註冊成功，頁面自動登入倒數計時
    const [countdown, setCountdown] = useState({
        second: 0,
        timer: null,
    });

    // Modal - 註冊成功，Login 狀態
    const [loginState, setLoginState] = useState({
        process: false,
        error: false,
    });

    // 清除 formData
    const clearFormData = () => {
        setFormData((formData) => ({
            ...formData,
            userName: '',
            userpwd: '',
            userpwdOld: '',
            userpwdConfirm: '',
            firstName: '',
            lastName: '',
            countryCode: '',
            phoneNumber: '',
            mobilePhone: '',
            custToken: '',
        }));
        setFormError((item) => ({
            ...item,
            userName: false,
            userNameAvailable: false,
            userpwd: false,
            userpwdOld: false,
            userpwdConfirm: false,
            userpwdDifference: false,
            firstName: false,
            firstNameLength: false,
            lastName: false,
            lastNameLength: false,
            countryCode: false,
            phoneNumber: false,
        }));
        setLoginTempData({
            password: '',
            custToken: '',
        });
    };

    const value = {
        currentStep,
        setCurrentStep,
        formData,
        setFormData,
        formError,
        setFormError,
        registerResponse,
        setRegisterResponse,
        handleInputChange,
        countdown,
        setCountdown,
        registerLoading,
        setRegisterLoading,
        loginState,
        setLoginState,
        loginTempData,
        setLoginTempData,
        clearFormData,
    };

    return (
        <RegisterContext.Provider value={value}>
            {children}
        </RegisterContext.Provider>
    );
};
